import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { PROPERTY_TYPES, TENURES } from "@/lib/constants";
import { Field, Select, SubmitButton } from "@/components/site/FormBits";
import { ImageUploader } from "@/components/admin/ImageUploader";

const PROPERTY_EMPTY = { title: "", area: "", postcode_district: "", price: "", property_type: "", bedrooms: "", bathrooms: "",
  tenure: "", description: "", features: "", images: [], status: "available", internal_notes: "" };
const DEAL_EMPTY = { title: "", area: "", sale_price: "", days_to_complete: "", completed_date: "", summary: "", images: [] };

const num = (v) => (v === "" || v == null ? null : Number(v));
const toForm = (kind, item) => {
  const base = kind === "properties" ? PROPERTY_EMPTY : DEAL_EMPTY;
  if (!item) return base;
  const f = { ...base };
  Object.keys(base).forEach((k) => { f[k] = item[k] ?? base[k]; });
  if (kind === "properties") f.features = (item.features || []).join(", ");
  return f;
};

const toPayload = (kind, f) => kind === "properties"
  ? { ...f, price: Number(f.price), bedrooms: Number(f.bedrooms), bathrooms: num(f.bathrooms),
      features: f.features.split(",").map((s) => s.trim()).filter(Boolean) }
  : { ...f, sale_price: num(f.sale_price), days_to_complete: num(f.days_to_complete) };

const PropertyFields = ({ f, set }) => (
  <>
    <div className="grid sm:grid-cols-3 gap-4">
      <Field label="Price (£)" required><input data-testid="listing-price" type="number" min="0" required value={f.price} onChange={set("price")} className="field-input" /></Field>
      <Field label="Bedrooms" required><input data-testid="listing-bedrooms" type="number" min="0" required value={f.bedrooms} onChange={set("bedrooms")} className="field-input" /></Field>
      <Field label="Bathrooms"><input data-testid="listing-bathrooms" type="number" min="0" value={f.bathrooms} onChange={set("bathrooms")} className="field-input" /></Field>
    </div>
    <div className="grid sm:grid-cols-3 gap-4">
      <Field label="Type" required><Select testId="listing-type" required options={PROPERTY_TYPES} value={f.property_type} onChange={set("property_type")} /></Field>
      <Field label="Tenure"><Select testId="listing-tenure" options={TENURES} value={f.tenure} onChange={set("tenure")} /></Field>
      <Field label="Status"><select data-testid="listing-status" value={f.status} onChange={set("status")} className="field-input field-select">
        <option value="available">Available</option><option value="under_offer">Under offer</option><option value="sold">Sold (hidden)</option>
      </select></Field>
    </div>
    <Field label="Features" hint="Comma separated, e.g. Garden, No onward chain"><input data-testid="listing-features" value={f.features} onChange={set("features")} className="field-input" /></Field>
    <Field label="Description"><textarea data-testid="listing-description" rows={3} value={f.description} onChange={set("description")} className="field-input h-auto py-3" /></Field>
    <Field label="Internal notes" hint="Never shown publicly - e.g. seller reference"><textarea data-testid="listing-internal-notes" rows={2} value={f.internal_notes || ""} onChange={set("internal_notes")} className="field-input h-auto py-3" /></Field>
  </>
);

const DealFields = ({ f, set }) => (
  <>
    <div className="grid sm:grid-cols-3 gap-4">
      <Field label="Sale price (£)" hint="Optional"><input data-testid="listing-sale-price" type="number" min="0" value={f.sale_price ?? ""} onChange={set("sale_price")} className="field-input" /></Field>
      <Field label="Days to complete" hint="Optional"><input data-testid="listing-days" type="number" min="0" value={f.days_to_complete ?? ""} onChange={set("days_to_complete")} className="field-input" /></Field>
      <Field label="Completed" hint="e.g. March 2026"><input data-testid="listing-completed-date" value={f.completed_date || ""} onChange={set("completed_date")} className="field-input" /></Field>
    </div>
    <Field label="Summary"><textarea data-testid="listing-summary" rows={3} value={f.summary || ""} onChange={set("summary")} className="field-input h-auto py-3" /></Field>
  </>
);

export const ListingDialog = ({ kind, item, open, onOpenChange, onSaved }) => {
  const [f, setF] = useState(toForm(kind, item));
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) setF(toForm(kind, item)); }, [open, kind, item]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const path = kind === "properties" ? "/admin/properties" : "/admin/deals";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = toPayload(kind, f);
      if (item) await api.put(`${path}/${item.id}`, payload);
      else await api.post(path, payload);
      toast.success(item ? "Saved" : "Added");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FBFBF9] rounded-none sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="listing-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium">
            {item ? "Edit" : "Add"} {kind === "properties" ? "property" : "completed deal"}
          </DialogTitle>
          <DialogDescription>First photo is used as the cover image.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Title" required><input data-testid="listing-title" required value={f.title} onChange={set("title")} className="field-input" /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Area" required><input data-testid="listing-area" required value={f.area} onChange={set("area")} className="field-input" placeholder="e.g. Clapham" /></Field>
            {kind === "properties" && <Field label="Postcode district"><input data-testid="listing-postcode" value={f.postcode_district || ""} onChange={set("postcode_district")} className="field-input" placeholder="e.g. SW4" /></Field>}
          </div>
          {kind === "properties" ? <PropertyFields f={f} set={set} /> : <DealFields f={f} set={set} />}
          <Field label="Photos"><ImageUploader images={f.images} onChange={(images) => setF({ ...f, images })} /></Field>
          <SubmitButton loading={loading} testId="listing-save-btn" className="w-full">Save</SubmitButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};
